import {
  RiskFactor as CanonicalRiskFactor,
  RiskLevel
} from 'src/app/shared/risk-factors-report/risk-factors.model';
import { ClinicalContextItem, RiskGroup } from './medical-history-risk-factor.schema';

/**
 * Config tables for the medical-history risk-summary pipeline. The runner
 * (`MedicalHistoryRiskSummaryService`) reads these to evaluate, group, enrich,
 * and render risk factors. Adding or tuning a factor edits this file only —
 * the service stays a pure runner.
 */

// ── Helpers used to define SUPPLEMENTAL_RISK_FACTORS ─────────────────────────

/**
 * "Patient has X" — fires when the form value is exactly `true`.
 * Used for medical-condition checkboxes (rosacea, glaucoma, sclerosis…).
 */
function booleanPresenceFactor(displayName: string, riskLevel: RiskLevel): CanonicalRiskFactor {
  return {
    name: displayName,
    indicators: [{ condition: true, operator: '=', riskLevel }]
  };
}

/**
 * "Patient picked one of these radio values" — fires when form value matches
 * any of the given trigger strings. Each trigger contributes its own indicator.
 */
function radioTriggerFactor(
  displayName: string,
  triggers: ReadonlyArray<{ value: string; riskLevel: RiskLevel }>
): CanonicalRiskFactor {
  return {
    name: displayName,
    indicators: triggers.map(trigger => ({
      condition: trigger.value,
      operator: '=',
      riskLevel: trigger.riskLevel
    }))
  };
}

/**
 * "Slider crossed a numeric threshold" — fires the FIRST matching tier.
 * Operator decides direction: `>=`/`>` for "high value = bad", `<=`/`<` for
 * "low value = bad" (poor diet, low water intake, etc.).
 */
function sliderTierFactor(
  displayName: string,
  operator: '>' | '>=' | '<' | '<=',
  tiers: ReadonlyArray<{ threshold: number; riskLevel: RiskLevel }>
): CanonicalRiskFactor {
  return {
    name: displayName,
    indicators: tiers.map(tier => ({
      condition: tier.threshold,
      operator,
      riskLevel: tier.riskLevel
    }))
  };
}

/**
 * "Free-text field has any non-empty content" — fires on `!empty`.
 */
function freeTextPresenceFactor(displayName: string, riskLevel: RiskLevel): CanonicalRiskFactor {
  return {
    name: displayName,
    indicators: [{ condition: '', operator: '!empty', riskLevel }]
  };
}

// ── Ocular-surgery keyword classifier ────────────────────────────────────────

export type OcularHistoryPriority = 'HIGH' | 'MODERATE';

export interface OcularHistoryKeywordEntry {
  keywords: string[];
  priority: OcularHistoryPriority;
}

// ── Exported config tables ───────────────────────────────────────────────────

/**
 * Service-local supplement for medical-history form fields that neither
 * `modifiableRiskFactors` nor `nonModifiableRiskFactors` covers. Uses the
 * canonical `RiskFactor` / `Indicator` types — no new types, no parallel
 * schema. Severity follows JSON crosswalk: High→high, Moderate To High→high,
 * Moderate→medium, Mild→low.
 */
export const SUPPLEMENTAL_RISK_FACTORS: Record<string, CanonicalRiskFactor> = {
  // ── Medical conditions (boolean presence) ──────────────────────────────
  rosacea:                booleanPresenceFactor('Rosacea',                                 'medium'),
  psoriasis:              booleanPresenceFactor('Psoriasis',                               'medium'),
  glaucoma:               booleanPresenceFactor('Glaucoma',                                'medium'),
  sclerosis:              booleanPresenceFactor('Sclerosis (multiple sclerosis)',          'medium'),
  cancer:                 booleanPresenceFactor('Cancer / chemotherapy',                   'medium'),
  sarcoidosis:            booleanPresenceFactor('Sarcoidosis',                             'medium'),
  hepatitis:              booleanPresenceFactor('Hepatitis',                               'medium'),
  irritableBowelSyndrome: booleanPresenceFactor('Irritable bowel syndrome (IBS)',          'medium'),
  vitiligo:               booleanPresenceFactor('Vitiligo',                                'medium'),
  gout:                   booleanPresenceFactor('Gout',                                    'medium'),
  polycysticOvary:        booleanPresenceFactor('Polycystic ovary syndrome',               'medium'),
  asthma:                 booleanPresenceFactor('Asthma',                                  'medium'),
  hypertension:           booleanPresenceFactor('Hypertension',                            'medium'),
  hypercholesterolemia:   booleanPresenceFactor('High cholesterol (hypercholesterolemia)', 'medium'),
  stroke:                 booleanPresenceFactor('Stroke',                                  'medium'),
  liverDisease:           booleanPresenceFactor('Liver disease',                           'medium'),
  renalFailure:           booleanPresenceFactor('Renal failure / CKD',                     'medium'),
  hiv:                    booleanPresenceFactor('HIV',                                     'medium'),
  migraine:               booleanPresenceFactor('Migraine',                                'medium'),
  dandruff:               booleanPresenceFactor('Dandruff',                                'medium'),
  pelvicPain:             booleanPresenceFactor('Pelvic pain',                             'low'),
  fertility:              booleanPresenceFactor('Fertility issues',                        'low'),
  htlv:                   booleanPresenceFactor('HTLV',                                    'low'),
  chronic:                booleanPresenceFactor('Chronic widespread pain syndrome',        'medium'),
  psychiatricConditions:  booleanPresenceFactor('Psychiatric conditions',                  'medium'),
  scleroderma:            booleanPresenceFactor('Scleroderma',                             'high'),
  boneMarrow:             booleanPresenceFactor('Bone marrow transplantation',             'high'),

  // ── Checkbox conditions ────────────────────────────────────────────────
  postmenopausal:            booleanPresenceFactor('Postmenopausal',                       'medium'),
  menopausal:                booleanPresenceFactor('Currently experiencing menopause',     'medium'),
  hormoneReplacementTherapy: booleanPresenceFactor('Hormone replacement therapy (HRT)',    'medium'),
  botoxInjections:           booleanPresenceFactor('Botox injections',                     'low'),
  thyroid:                   booleanPresenceFactor('Past diagnosis of thyroid disease',    'medium'),

  // ── Radio conditions (one indicator per trigger value) ─────────────────
  tearingQuality: radioTriggerFactor('Tearing quality (reflex tearing)', [
    { value: 'indoorAndOutdoor', riskLevel: 'medium' },
    { value: 'outdoorOnly',      riskLevel: 'medium' }
  ]),
  symptomSeverityOccurrence: radioTriggerFactor('Symptom timing pattern', [
    { value: 'dayEnd',       riskLevel: 'medium' },
    { value: 'bothStartEnd', riskLevel: 'medium' },
    { value: 'dayStart',     riskLevel: 'medium' }
  ]),
  bothersomeEye: radioTriggerFactor('Asymmetric eye symptoms', [
    { value: 'oneEyeOnly', riskLevel: 'medium' },
    { value: 'moreRight',  riskLevel: 'medium' },
    { value: 'moreLeft',   riskLevel: 'medium' }
  ]),

  // ── Sliders (numeric thresholds; first matching tier wins) ─────────────
  symptomSeverity:        sliderTierFactor('Severity of symptoms',         '>=', [
    { threshold: 70, riskLevel: 'high' },
    { threshold: 40, riskLevel: 'medium' }
  ]),
  symptomFrequency:       sliderTierFactor('Frequency of symptoms',        '>=', [
    { threshold: 60, riskLevel: 'medium' },
    { threshold: 30, riskLevel: 'low' }
  ]),
  sensitivityToLight:     sliderTierFactor('Sensitivity to light',         '>=', [
    { threshold: 60, riskLevel: 'medium' },
    { threshold: 30, riskLevel: 'low' }
  ]),
  sensitivityToWind:      sliderTierFactor('Sensitivity to wind',          '>=', [{ threshold: 50, riskLevel: 'low' }]),
  impactOfSymptomsOnLife: sliderTierFactor('Impact of symptoms on daily life', '>=', [
    { threshold: 60, riskLevel: 'medium' },
    { threshold: 30, riskLevel: 'low' }
  ]),
  impactOfSymptomsOnMood: sliderTierFactor('Impact of symptoms on mood',   '>=', [{ threshold: 50, riskLevel: 'low' }]),
  // Skip-if-zero guarded in evaluator (0 means N/A for this slider).
  dietQuality:            sliderTierFactor('Diet quality (poor)',          '<=', [{ threshold: 30, riskLevel: 'medium' }]),

  // ── Duration objects (evaluated by .total) ─────────────────────────────
  // symptomDuration is handled in evaluator below (needs .total unwrapping)

  // ── Free-text presence cards ───────────────────────────────────────────
  triggeringEvent:              freeTextPresenceFactor('Triggering event reported',              'medium'),
  previousTreatmentIssues:      freeTextPresenceFactor('Previous treatment issues reported',     'medium'),
  autoImmuneConditions:         freeTextPresenceFactor('Autoimmune condition reported',          'medium'),
  otherAllergies:               freeTextPresenceFactor('Other allergies reported',               'low'),
  pastDryEyeTreatmentsFreeText: freeTextPresenceFactor('Past dry-eye treatments reported',       'medium'),
  historyOfPresentingIllness:   freeTextPresenceFactor('History of presenting illness reported', 'medium'),
  changesToEyes:                freeTextPresenceFactor('Changes to eyes reported',               'medium'),
  medicalConditionsFreeText:    freeTextPresenceFactor('Other medical conditions reported',      'medium'),
  otherConditionsFreeText:      freeTextPresenceFactor('Other conditions reported',              'low'),

  // ── Work hours (lifestyle numeric free-text) ──────────────────────────
  workHoursPerDayFreeText:      sliderTierFactor('Work hours per day (more than 8)', '>', [{ threshold: 8, riskLevel: 'medium' }])
};

/**
 * Fields whose value is a `{ total, unit }` object (not a scalar).
 * compareValues can't reach into them, so we unwrap to `.total` before evaluating.
 */
export const DURATION_OBJECT_FACTORS: Record<string, CanonicalRiskFactor> = {
  symptomDuration: {
    name: 'Symptom duration (more than 6 months)',
    indicators: [{ condition: 6, operator: '>', riskLevel: 'low' }]
  }
};

/**
 * Sliders where 0 means "untouched / N/A" and must NOT fire any indicator.
 * Every slider in the medical history form uses 0 as the visual default
 * "the patient hasn't moved this," so evaluating any of them at 0 would be
 * a false positive — `<=` rules fire low-risk on a still-zero slider, and
 * even `>=` rules can be misleading on reload where the form value gets
 * patched to 0 from the backend without any real patient input. The single
 * exception is `eyeTearingQuantity`, whose sentinel is -10 — there, 0 is a
 * meaningful patient answer ("no tearing") and must keep firing.
 */
export const SKIP_IF_ZERO_KEYS = new Set<string>([
  'dietQuality',
  'cityHumidityLevel',
  'cityPollutionLevel',
  'healthQuality',
  'stressLevel',
  'sensitivityToLight',
  'sensitivityToWind',
  'impactOfSymptomsOnMood',
  'impactOfSymptomsOnLife',
  'symptomFrequency',
  'symptomSeverity',
  'eyeItchiness'
]);

/**
 * Maps each canonical / supplemental factor key to the correct RiskGroup so
 * the quick-scan component can route items to the right category row.
 * Keys absent from this map default to 'medicalConditions' (Conditions bucket).
 */
export const KEY_TO_GROUP: Partial<Record<string, RiskGroup>> = {
  // ── Lifestyle & environment ──────────────────────────────────────────────
  smoking:                         'lifestyle',
  vaping:                          'lifestyle',
  alcohol:                         'lifestyle',
  screenTime:                      'lifestyle',
  sleepPerNight:                   'lifestyle',
  sleepingPosition:                'lifestyle',
  exercise:                        'lifestyle',
  stressLevel:                     'lifestyle',
  healthQuality:                   'lifestyle',
  dietQuality:                     'lifestyle',
  caffeinatedDrinksPerDayFreeText: 'lifestyle',
  waterPerDayFreeText:             'lifestyle',
  urbanAreaTimePerWeekFreeText:    'lifestyle',
  timeSpentOutdoorsFreeText:       'lifestyle',
  cityHumidityLevel:               'lifestyle',
  cityPollutionLevel:              'lifestyle',
  eyeRubbingFrequency:             'lifestyle',
  workHoursPerDayFreeText:         'lifestyle',

  // ── Ocular symptoms (slider) ─────────────────────────────────────────────
  eyeDryness:                      'slider',
  eyeRedness:                      'slider',
  eyeFatigue:                      'slider',
  eyeItchiness:                    'slider',
  eyeMucous:                       'slider',
  eyeTearingQuantity:              'slider',
  visionAffected:                  'slider',
  currentContactLensDiscomfort:    'slider',
  crustyOrDischarge:               'slider',
  enlargedGlands:                  'slider',
  eyesSwollen:                     'slider',
  symptomsRednessGreaterInOneEye:  'slider',
  symptomSeverity:                 'slider',
  symptomFrequency:                'slider',
  sensitivityToLight:              'slider',
  sensitivityToWind:               'slider',
  impactOfSymptomsOnLife:          'slider',
  impactOfSymptomsOnMood:          'slider',

  // ── Symptom radio conditions ─────────────────────────────────────────────
  tearingQuality:                  'radioCondition',
  symptomSeverityOccurrence:       'radioCondition',
  bothersomeEye:                   'radioCondition',

  // ── Free-text presence → ocular / history bucket ─────────────────────────
  triggeringEvent:                 'freeTextPresent',
  previousTreatmentIssues:         'freeTextPresent',
  pastDryEyeTreatmentsFreeText:    'freeTextPresent',
  historyOfPresentingIllness:      'freeTextPresent',
  changesToEyes:                   'freeTextPresent',
  medicalConditionsFreeText:       'freeTextPresent',
  otherConditionsFreeText:         'freeTextPresent',
  otherAllergies:                  'freeTextPresent',
};

/**
 * Allergic / environmental symptom keys. If 1+ are truthy, the per-field hits
 * are removed and a single `allergicEnvironmentalSymptoms` card replaces them.
 * Evidence: allergic rhinitis OR 1.58 for DED (PubMed 28124649);
 * AC comorbidity 47.2% (PMC9267454).
 */
export const ALLERGIC_KEYS = [
  'runnyNose',
  'allergySymptoms',
  'noseBurningSensation',
  'throatIrritation',
  'breathingDifficulty',
  'chestTightness',
  'skinDrynessAndItchiness'
];

/**
 * Systemic symptom keys. Same pattern — 1+ truthy collapses per-field hits
 * into one `systemicSymptoms` card.
 */
export const SYSTEMIC_KEYS = [
  'headaches',
  'dizziness',
  'difficultyConcentrating',
  'forgetfulness',
  'fatigue',
  'irritability',
  'nausea',
  'bodyAches',
  'fever',
  'chills'
];

/**
 * Friendly labels for the per-field keys inside ALLERGIC_KEYS / SYSTEMIC_KEYS.
 * Used as the `detail` text on the collapsed allergic / systemic cluster cards.
 */
export const SYMPTOM_CLUSTER_LABELS: Record<string, string> = {
  runnyNose:               'Runny nose',
  allergySymptoms:         'Allergy symptoms',
  noseBurningSensation:    'Nose burning',
  throatIrritation:        'Throat irritation',
  breathingDifficulty:     'Breathing difficulty',
  chestTightness:          'Chest tightness',
  skinDrynessAndItchiness: 'Skin dryness/itchiness',
  headaches:               'Headaches',
  dizziness:               'Dizziness',
  difficultyConcentrating: 'Difficulty concentrating',
  forgetfulness:           'Forgetfulness',
  fatigue:                 'Fatigue',
  irritability:            'Irritability',
  nausea:                  'Nausea',
  bodyAches:               'Body aches',
  fever:                   'Fever',
  chills:                  'Chills'
};

/**
 * Parent risk-card key → free-text form field(s) whose content enriches the
 * parent's rendered `detail`. Lets the summary show the patient's own words
 * (e.g. "Hypothyroidism since 2018") underneath the flagged condition card.
 */
export const FREE_TEXT_DETAIL_MAP: Record<string, string[]> = {
  thyroid:                  ['thyroidConditionFreeText'],
  chronic:                  ['chronicPainConditionFreeText'],
  vitaminDeficiency:        ['vitaminDeficiencyConditionFreeText'],
  sexSteroidDeficiency:     ['sexSteroidDeficiencyConditionFreeText'],
  psychiatricConditions:    ['psychiatricConditionsFreeText'],
  contactLens:              ['otherConditionsFreeText'],
  skinProductEyeIrritation: ['faceCareAndMakeUpProductsFreeText'],
  cityPollutionLevel:       ['urbanAreaTimePerWeekFreeText', 'timeSpentClosedEnvironmentsFreeText'],
  cityHumidityLevel:        ['timeSpentOutdoorsFreeText'],
  dietQuality:              ['dietFreeText'],
  seasonalAllergies:        ['otherAllergies'],
  drugAllergies:            ['otherAllergies'],
  medications:              ['currentOcularMedicationsFreeText', 'pastOcularMedicationsFreeText', 'otherMedications', 'patientEnteredMedicationsFreeText']
};

/**
 * Builds the enrichment string for a card key from FREE_TEXT_DETAIL_MAP.
 * Returns undefined when no mapped field is populated, so the card stays clean.
 * Drug[] entries (medications) render as comma-joined names.
 */
export function buildDetail(key: string, formValue: any): string | undefined {
  const fieldNames = FREE_TEXT_DETAIL_MAP[key];
  if (!fieldNames || fieldNames.length === 0) { return undefined; }

  const parts: string[] = [];
  for (const fieldName of fieldNames) {
    const raw = formValue[fieldName];
    if (raw === null || raw === undefined) { continue; }

    if (Array.isArray(raw)) {
      const drugNames = raw
        .map(item => (item && typeof item === 'object' && item.name) ? String(item.name).trim() : '')
        .filter(name => name.length > 0);
      if (drugNames.length > 0) { parts.push(drugNames.join(', ')); }
      continue;
    }

    if (typeof raw === 'string' && raw.trim().length > 0) {
      parts.push(raw.trim());
    }
  }

  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * Patient Context items — not risk factors, just reference info shown alongside
 * the risk summary. Split into a primary list and a collapsible Fitzpatrick/sun
 * profile sub-panel.
 */
export const CLINICAL_CONTEXT_ITEMS: ClinicalContextItem[] = [
  // ── Primary ───────────────────────────────────────────────────────────────
  { key: 'occupation',       label: 'Occupation',        subGroup: 'primary',     formatter: 'text' },
  { key: 'hobbies',          label: 'Hobbies',           subGroup: 'primary',     formatter: 'text' },
  { key: 'lastEyeExam',      label: 'Last eye exam',     subGroup: 'primary',     formatter: 'text' },
  { key: 'wearsGlasses',     label: 'Wears glasses',     subGroup: 'primary',     formatter: 'boolean' },
  { key: 'sleepingPosition', label: 'Sleeping position', subGroup: 'primary',     formatter: 'radio' },

  // ── Fitzpatrick / sun profile ─────────────────────────────────────────────
  { key: 'eyeColor',                 label: 'Eye color',                    subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'naturalHairColor',         label: 'Natural hair color',           subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'skinColor',                label: 'Skin color',                   subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'freckles',                 label: 'Freckles',                     subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'longSunExposure',          label: 'Reaction to long sun exposure', subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'darkerAfterSunExposure',   label: 'Skin darkens after sun',       subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'tanIntensity',             label: 'Tan intensity',                subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'faceSunSensitivity',       label: 'Face sun sensitivity',         subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'sunTanExposureFrequency',  label: 'Sun/tan exposure frequency',   subGroup: 'fitzpatrick', formatter: 'radio' },
  { key: 'lastSunExposure',          label: 'Last sun exposure',            subGroup: 'fitzpatrick', formatter: 'radio' }
];

/**
 * Keyword-based severity escalator for `previousOcularSurgeryFreeText`.
 * HIGH surgeries (LASIK/PRK, cataract, retinal, trauma, pterygium, glaucoma
 * surgery, corneal transplant) escalate the card to high. MODERATE keywords
 * (strabismus, eyelid, orthokeratology) keep it at moderate.
 */
export const OCULAR_SURGERY_KEYWORDS: OcularHistoryKeywordEntry[] = [
  { priority: 'HIGH',     keywords: ['lasik', 'prk', 'refractive surgery'] },
  { priority: 'HIGH',     keywords: ['cataract surgery', 'cataract extraction'] },
  { priority: 'HIGH',     keywords: ['retinal surgery', 'vitrectomy', 'scleral buckle'] },
  { priority: 'HIGH',     keywords: ['trauma', 'injury', 'corneal abrasion', 'chemical burn'] },
  { priority: 'HIGH',     keywords: ['pterygium', "surfer's eye", 'surfer eye'] },
  { priority: 'HIGH',     keywords: ['glaucoma surgery', 'trabeculectomy', 'tube shunt'] },
  { priority: 'HIGH',     keywords: ['corneal transplant', 'keratoplasty', 'dsek', 'dmek', 'pkp'] },
  { priority: 'MODERATE', keywords: ['strabismus surgery', 'eye muscle surgery'] },
  { priority: 'MODERATE', keywords: ['ptosis surgery', 'eyelid surgery', 'blepharoplasty'] },
  { priority: 'MODERATE', keywords: ['orthokeratology', 'ortho-k'] }
];
